import React from 'react';
import classNames from 'classnames';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import { updateApplication } from '../../../actions/applications';
import { getRoute, getSession, loading } from '../../../actions/sessions';
import { asyncActions, handleAsyncError } from '../../../actions/general';
import { validateEin } from '../../../actions/companies';
import { validatePhone } from '../../../actions/users';
import { states, business_types, formStyles, poBoxRegex } from '../../../constants/SiteVariables';
import ApplicationRecord from '../../../records/application';
import useRouteStatus from '../../../hooks/useRouteStatus';
import useValidateRoute from '../../../hooks/useValidateRoute';
import useSessionEntity from '../../../hooks/useSessionEntity';
import Error from '../../shared/Error';
import ColumnInput from '../../shared/ColumnInput';
import StickyInput from '../../shared/StickyInput';
import StickySelect from '../../shared/StickySelect';
import StickyIndustryTypeAhead from '../../shared/StickyIndustryTypeAhead';

export default function Business({location}) {
  const dispatch = useDispatch();

  const isLoading = useSelector(state => state.sessions.get('loading'));
  const application = useSessionEntity('application');

  useRouteStatus(location);

  const save = values => {
    dispatch(loading(true));
    dispatch(asyncActions(
      updateApplication(new ApplicationRecord(values)),
      getSession({ cart: false, prequal: false, merchant: false }),
      getRoute()
    ))
    .then(res => dispatch(loading(false)));
  }

  const checkEin = async (val) => {
    try {
      const res = await dispatch(validateEin(val));
      return res.result;
    } catch(e) {
      dispatch(handleAsyncError(e));
    }
  }

  const checkPhone = async (val) => {
    try {
      const res = await dispatch(validatePhone(val));
      return res.result;
    } catch (e) {
      dispatch(handleAsyncError(e));
    }
  }

  if (!useValidateRoute({ apply: true, company: true })) return (<></>);

  return (
    <div id="content">
      <div className="has-text-centered mb-5">
        <div className="is-size-4 has-text-weight-semibold">Please confirm your business details</div>
        <div className="has-text-weight-semibold text-green-highlight">Remember: applying will <u>not</u> affect your personal credit</div>
      </div>

      <Error />

      <Formik
        // the values that come off the API are null
        // and need to be reset here to cooperate w/ formik
        initialValues={application.toJS()}
        validate={async (values) => {
          const errors = {};
          const addNested = (field, msg) => errors['business_address'] = { ...errors['business_address'], [field]: msg };

          if (!values.business_address.address1 || values.business_address.address1.trim().length < 2) {
            addNested('address1', '* Required');
          }

          if (poBoxRegex.test(values.business_address.address1)) {
            addNested('address1', '* P.O. Boxes are not accepted.');
          } else if (poBoxRegex.test(values.business_address.address2)){
            addNested('address2', '* P.O. Boxes are not accepted.');
          }

          if (!values.business_address.city || values.business_address.city.trim().length < 2) {
            addNested('city', '* Required');
          }
          if (!values.business_address.state) addNested('state', '* Required');

          if (!values.business_address.zip) {
            addNested('zip', '* Required');
          } else if(values.business_address.zip.trim().length < 5) {
            addNested('zip', '* Must be 5 characters in length');
          }

          if (!values.raw_annual_revenue) {
            errors.raw_annual_revenue = '* Required';
          } else if (parseInt(values.raw_annual_revenue.replace(',','').replace(' ','')) < 1) {
            errors.raw_annual_revenue = '* A valid number is required';
          }

          if (!values.business_type) errors.business_type = '* Required';
          if (!values.industry) errors.industry = '* Required';

          if (!values.ein) {
            errors.ein = '* Required';
          } else if (values.ein.trim().length < 10) {
            errors.ein = '* Must be 9 characters in length';
          }

          if (!values.business_phone) {
            errors.business_phone = '* Required';
          } else if (values.business_phone.replace(/[^\d]/g, '').length < 10) {
            errors.business_phone = '* Please enter a valid phone number';
          }

          return errors;
        }}
        onSubmit={async (values, { setFieldError }) => {
          dispatch(loading(true));

          const einCheck = await checkEin(values.ein);
          const phoneCheck = await checkPhone(values.phone);

          if (!phoneCheck.valid) {
            setFieldError('business_phone', '* Please enter a valid phone number');
          }

          if (!einCheck.valid) {
            setFieldError('ein', einCheck.error);
          }
          
          einCheck.valid && phoneCheck.valid ? save(values) : dispatch(loading(false));
        }}
      >
        {({ isSubmitting, errors, setFieldValue }) => (
          <Form role="form">
            <ColumnInput error={errors.business_address && errors.business_address.address1}>
              <StickyInput
                id="business_address.address1"
                placeholder="Business Street Address"
              />
            </ColumnInput>

            <ColumnInput>
              <StickyInput
                id="business_address.address2"
                placeholder="Suite or Floor"
                styles="is-mobile"
              />
            </ColumnInput>

            <ColumnInput error={errors.business_address && errors.business_address.city}>
              <StickyInput
                id="business_address.city"
                placeholder="City"
              />
            </ColumnInput>
            
            <ColumnInput error={errors.business_address && (errors.business_address.state || errors.business_address.zip)}>
              <StickySelect
                id="business_address.state"
                placeholder="State"
                options={states}
              />

              <StickyInput
                id="business_address.zip"
                mask="99999"
                placeholder="Zip"
              />
            </ColumnInput>

            <ColumnInput error={errors.raw_annual_revenue}>
              <StickyInput
                inputMode="numeric"
                id="raw_annual_revenue"
                placeholder="$ Total Annual Revenue"
                mask={Number}
                max={99999999}
                thousandsSeparator=","
                scale={0}
              />
            </ColumnInput>

            <ColumnInput error={errors.business_type}>
              <StickySelect
                id="business_type"
                options={business_types}
                placeholder="Business Type"
              />
            </ColumnInput>

            <ColumnInput error={errors.industry}>
              <StickyIndustryTypeAhead
                id="industry"
                placeholder="Industry"
                setFieldValue={setFieldValue}
              />
            </ColumnInput>

            <ColumnInput error={errors.business_phone}>
              <StickyInput
                id="business_phone"
                mask="(999) 999-9999"
                placeholder="Business Phone Number"
              />
            </ColumnInput>

            <ColumnInput error={errors.ein}>
              <StickyInput
                inputMode="numeric"
                id="ein"
                mask="99-9999999"
                placeholder="EIN" />
            </ColumnInput>

            <button
              type="submit"
              role="submit"
              disabled={isLoading}
              className={classNames('button is-primary is-large is-fullwidth has-text-weight-bold', {
                'is-loading': isLoading
              })}>
              Next
            </button>
          </Form>
        )}
      </Formik>
    </div>
  )
}
